import { DateTime } from 'luxon';

import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { SAR } from 'dashboard/models/sar';
import SARStatusSticker from 'dashboard/views/DashboardSARsView/SARStatusSticker';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';

export interface SARsTableProps {
  sars: SAR[];
  sortParams: SortParams;
  hasUsedFilters: boolean;
  isLoading: boolean;
  handleSortUpdate: (sortParams: SortParams) => void;
}

const SARsTable = ({
  sars = [],
  sortParams,
  isLoading,
  hasUsedFilters,
  handleSortUpdate,
}: SARsTableProps) => {
  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const sarRows = sars.map((sar) => {
    return <SARTableRow key={sar.id} sar={sar} />;
  });

  const nullStateText =
    'No SARs have been filed. Create and file a SAR to report suspicious activity to FinCEN.';

  return (
    <TableLayout
      isLoading={isLoading}
      showHeader={true}
      isEmpty={sars.length === 0}
      nullState={<TableNullState hasUsedFilters={hasUsedFilters}>{nullStateText}</TableNullState>}
      headerTR={
        <>
          <TableTH id='sars-table__header--name'>Name</TableTH>
          <TableTH id='sars-table__header--bsa-id'>BSA ID</TableTH>
          <TableTH id='sars-table__header--status'>Status</TableTH>
          <TableTH
            id='sars-table__header--created-at'
            canSort={true}
            isSorted={sortParams.sort === 'created_at'}
            isDescending={sortParams.shouldSortDescending}
            onClick={() => handleColumnHeaderClick('created_at')}
          >
            {`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
          </TableTH>
        </>
      }
    >
      {sarRows}
    </TableLayout>
  );
};

interface SARTableRowProps {
  sar: SAR;
}

const SARTableRow = ({ sar }: SARTableRowProps) => {
  const { name, status, 'bsa-id': BSAId, 'created-at': createdAt } = sar.attributes;

  return (
    <TableLinkTR to={`/dashboard/sars/${sar.id}`} id={`sar-table-row__${sar.id}`}>
      <TableTD>{name}</TableTD>
      <TableTD>
        <MonoSpan>{BSAId}</MonoSpan>
      </TableTD>
      <TableTD>
        <SARStatusSticker status={status} />
      </TableTD>
      <TableTD>
        {createdAt ? DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT) : ''}
      </TableTD>
    </TableLinkTR>
  );
};

export default SARsTable;
